/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Data formatting and transformation functions for API responses
 */

// Interfaces for API response items
interface BgpNeighborItem {
  neighborAddress?: string;
  bgpNeighbor?: {
    neighborAddress?: string;
    peerAs?: number;
    operatingMode?: string;
    updatedAt?: string;
    id?: number;
    reflectorInstanceId?: number;
  };
  reflectorInstanceId?: number;
}

interface InterfaceItem {
  interface?: string;
  name?: string;
  ipAddress?: string;
  ip?: string;
  macAddress?: string;
  mac?: string;
  interfaceAddresses?: Array<{ ipAddress: string }>;
}

interface SubnetItem {
  subnet_formatted: string;
}

// Cloud Propagation Data Transformers
export const transformCloudPropagationData = (apiResponse: unknown): any[] => {
  const response = apiResponse as { data?: BgpNeighborItem[] } | BgpNeighborItem[];
  
  // Handle new API structure - wrapped in data property (current structure)
  if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
    return response.data.map((item: BgpNeighborItem) => ({
      bgpNeighborIp: item.neighborAddress || item.bgpNeighbor?.neighborAddress || 'N/A',
      peerAs: item.bgpNeighbor?.peerAs?.toString() || 'N/A',
      mode: (item.bgpNeighbor?.operatingMode?.toLowerCase() === 'enterprise' ? 'Enterprise' : 'Telco') as 'Enterprise' | 'Telco',
      lastUpdate: item.bgpNeighbor?.updatedAt ? new Date(item.bgpNeighbor.updatedAt).toLocaleString() : 'N/A',
      bgpNeighborId: item.bgpNeighbor?.id,
      reflectorInstanceId: item.bgpNeighbor?.reflectorInstanceId,
    }));
  }

  // Handle direct array response (fallback)
  if (Array.isArray(response)) {
    return response.map((item: BgpNeighborItem) => ({
      bgpNeighborIp: item.neighborAddress || item.bgpNeighbor?.neighborAddress || 'N/A',
      peerAs: item.bgpNeighbor?.peerAs?.toString() || 'N/A',
      mode: (item.bgpNeighbor?.operatingMode?.toLowerCase() === 'enterprise' ? 'Enterprise' : 'Telco') as 'Enterprise' | 'Telco',
      lastUpdate: item.bgpNeighbor?.updatedAt ? new Date(item.bgpNeighbor.updatedAt).toLocaleString() : 'N/A',
      bgpNeighborId: item.bgpNeighbor?.id,
      reflectorInstanceId: item.bgpNeighbor?.reflectorInstanceId,
    }));
  }

  return [];
};

// Cluster Cloud Propagation Data Transformer
export const transformClusterCloudPropagationData = (apiResponse: any): any[] => {
  // Handle new API structure - wrapped in data property (current structure)
  if (apiResponse?.data && Array.isArray(apiResponse.data)) {
    return apiResponse.data.map((item: BgpNeighborItem) => ({
      bgpNeighborIp: item.neighborAddress || item.bgpNeighbor?.neighborAddress || 'N/A',
      peerAs: item.bgpNeighbor?.peerAs?.toString() || 'N/A',
      mode: (item.bgpNeighbor?.operatingMode?.toLowerCase() === 'enterprise' ? 'Enterprise' : 'Telco') as 'Enterprise' | 'Telco',
      lastUpdate: item.bgpNeighbor?.updatedAt ? new Date(item.bgpNeighbor.updatedAt).toLocaleString() : 'N/A',
      bgpNeighborId: item.bgpNeighbor?.id,
      reflectorInstanceId: item.bgpNeighbor?.reflectorInstanceId,
    }));
  }

  // Handle direct array response (fallback)
  if (Array.isArray(apiResponse)) {
    return apiResponse.map((item: BgpNeighborItem) => ({
      bgpNeighborIp: item.neighborAddress || item.bgpNeighbor?.neighborAddress || 'N/A',
      peerAs: item.bgpNeighbor?.peerAs?.toString() || 'N/A',
      mode: (item.bgpNeighbor?.operatingMode?.toLowerCase() === 'enterprise' ? 'Enterprise' : 'Telco') as 'Enterprise' | 'Telco',
      lastUpdate: item.bgpNeighbor?.updatedAt ? new Date(item.bgpNeighbor.updatedAt).toLocaleString() : 'N/A',
      bgpNeighborId: item.bgpNeighbor?.id,
      reflectorInstanceId: item.bgpNeighbor?.reflectorInstanceId,
    }));
  }

  return [];
};

// Interface Mapping Functions
export const mapInterfaces = (interfaces: InterfaceItem[] = []): any[] => {
  return interfaces.map(iface => ({
    name: iface.interface || iface.name || '-',
    ip: iface.ipAddress || iface.ip || '-',
    mac: iface.macAddress || iface.mac || '-',
  }));
};

// Enhanced interface mapping for reflector details
export const mapReflectorInterfaces = (interfaces: InterfaceItem[] = []): any[] => {
  return interfaces.map((iface: InterfaceItem) => ({
    ...iface,
    ipAddress: Array.isArray(iface.interfaceAddresses)
      ? iface.interfaceAddresses.map((addr: any) => addr.ipAddress).join(', ')
      : '',
  }));
};

// Subnet Data Transformers
export const extractSubnets = (apiResponse: any): string[] => {
  if (apiResponse?.data?.data?.subnets) {
    return apiResponse.data.data.subnets.map((subnet: SubnetItem) => subnet.subnet_formatted);
  }
  return [];
};

// Route Tables and Network Interfaces Data Transformers
export const extractRouteTables = (apiResponse: any): string[] => {
  if (apiResponse?.data?.data?.route_tables) {
    return apiResponse.data.data.route_tables
      .map((item: any) => item.elementId)
      .filter((id: any) => id != null && id !== '');
  }
  return [];
};

export const extractNetworkInterfaces = (apiResponse: any): string[] => {
  if (apiResponse?.data?.data?.network_interfaces) {
    return apiResponse.data.data.network_interfaces
      .map((item: any) => item.elementId)
      .filter((id: any) => id != null && id !== '');
  }
  return [];
};

export const extractVpcs = (apiResponse: any): string[] => {
  if (apiResponse?.data?.data?.vpcs) {
    return apiResponse.data.data.vpcs.filter((vpc: any) => vpc != null && vpc !== '');
  }
  return [];
};

// Full API Data Extractors (for filtering)
export const extractFullRouteTablesData = (apiResponse: any): any[] => {
  return apiResponse?.data?.data?.route_tables || [];
};

export const extractFullNetworkInterfacesData = (apiResponse: any): any[] => {
  return apiResponse?.data?.data?.network_interfaces || [];
};

// Status Mapping Functions
export const mapAvailabilityToStatus = (available: boolean | number | null): string => {
  // Handle boolean (centralized usage)
  if (typeof available === 'boolean') {
    return available ? 'Available' : 'Unavailable';
  }
  
  // Handle number/null (legacy usage)
  if (available === 1) {
    return "Online";
  } else if (available === 0 || available === null) {
    return "Offline";
  }
  return "Offline"; // Default case
};

export const getStatusVariant = (status?: string): 'success' | 'warning' | 'error' | 'neutral' | 'default' => {
  if (!status) return 'neutral';
  
  switch (status.toLowerCase()) {
    // Centralized status values
    case 'available':
      return 'success';
    case 'unavailable':
      return 'error';
    case 'pending':
      return 'warning';
    // Legacy status values
    case 'online':
      return 'success';
    case 'offline':
      return 'error';
    case 'degraded':
      return 'warning';
    default:
      return 'neutral';
  }
};

// Date Formatting Functions
export const formatDateTimeUTC = (dateString?: string, useLegacyFormat = false): string => {
  if (!dateString) return "-";
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    
    if (useLegacyFormat) {
      // Use manual formatting for legacy compatibility (dd/MM/yyyy HH:mm:ss)
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } else {
      // Use toLocaleString for new format
      return date.toLocaleString();
    }
  } catch (error) {
    return "-";
  }
};

// Tag Processing Functions
export const processTags = (tags: any): string => {
  if (typeof tags === 'string') {
    return tags;
  }
  return '';
};

// Reflector Data Transformer
export const transformReflectorDetails = (responseData: any): any => {
  const mappedInterfaces = mapReflectorInterfaces(responseData.interfaces || []);
  const tagsString = processTags(responseData.tags);
  
  return {
    ...responseData,
    id: String(responseData.id),
    name: responseData.name || '',
    description: responseData.description || '',
    subId: responseData.subId || '',
    status: mapAvailabilityToStatus(responseData.available),
    cloud: responseData.data?.cloud ? responseData.data.cloud.toUpperCase() : '',
    region: responseData.data?.region || '',
    cluster: responseData.cluster?.name || (responseData.clusterId ? String(responseData.clusterId) : '—'),
    clusterId: responseData.cluster?.id ? String(responseData.cluster.id) : responseData.clusterId ? String(responseData.clusterId) : undefined,
    version: responseData.version || '',
    lastSync: responseData.lastUpdate || '-',
    createdAt: responseData.createdAt || '-',
    data: responseData.data,
    cloudElements: responseData.cloudElements || [],
    interfaces: mappedInterfaces,
    tags: tagsString,
  };
};
